import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { partnerApi } from "../lib/partnerAuth";
import { ChevronLeft, FileText, Download } from "lucide-react";
import { Button } from "../components/ui/button";

function fmtDate(d) { if (!d) return "—"; try { return new Date(d).toLocaleDateString(); } catch { return d; } }

const DOC_LABELS = {
  offer_letter: "Offer Letter",
  conditional_offer: "Conditional Offer",
  unconditional_offer: "Unconditional Offer",
  approval_letter: "Approval Letter",
  submission_letter: "Submission Letter",
  pal: "PAL",
  fee_receipt: "Fee Receipt",
  loa: "Letter of Acceptance",
};

export default function PartnerAppDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => { partnerApi.get(`/partner/applications/${id}`).then(r=>setData(r.data)); }, [id]);
  if (!data) return <div className="text-slate-500">Loading…</div>;
  const a = data.application;

  const download = async (f) => {
    const r = await partnerApi.get(`/partner/files/${f.id}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const link = document.createElement("a");
    link.href = url; link.download = f.original_filename || "file"; link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <Link to="/partner" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800"><ChevronLeft className="w-4 h-4" />Back to my students</Link>

      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="heading-font text-2xl font-bold text-slate-900">{a.student_name}</h1>
            <div className="text-sm text-slate-500 mt-1 mono">{a.application_id}</div>
            <div className="text-sm text-slate-600 mt-2">{a.college} · {a.program} · {a.intake}</div>
          </div>
          <div className="text-right">
            <div className="text-xs uppercase text-slate-500">Current stage</div>
            <div className="heading-font text-lg font-semibold text-slate-900">{a.stage || "—"}</div>
            <div className="text-xs text-slate-500 mt-2">BDM: <span className="text-slate-700">{a.bdm_name || "—"}</span></div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card label="Student Email" value={a.email} />
        <Card label="Student Phone" value={a.phone} />
        <Card label="Campus" value={a.campus} />
        <Card label="Received On" value={fmtDate(a.date_received)} />
        <Card label="Offer Sent" value={fmtDate(a.offer_sent_date)} />
        <Card label="Priority" value={a.priority} />
      </div>

      <div className="bg-white rounded-xl border border-slate-200">
        <div className="px-5 py-3 border-b border-slate-200 heading-font font-semibold flex items-center gap-2"><FileText className="w-4 h-4"/>Documents</div>
        <div className="divide-y divide-slate-100">
          {data.documents.length === 0 && <div className="p-5 text-sm text-slate-500">No documents available yet.</div>}
          {data.documents.map(f => (
            <div key={f.id} data-testid={`partner-doc-${f.id}`} className="flex items-center justify-between px-5 py-3">
              <div>
                <div className="text-sm font-medium text-slate-900">{DOC_LABELS[f.doc_type] || f.doc_type}</div>
                <div className="text-xs text-slate-500">{f.original_filename} · {fmtDate(f.created_at)}</div>
              </div>
              <Button size="sm" variant="outline" onClick={()=>download(f)}><Download className="w-4 h-4 mr-1"/>Download</Button>
            </div>
          ))}
        </div>
      </div>

      {data.enrollments.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-200 heading-font font-semibold">Enrollments</div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500"><tr><th className="text-left px-5 py-2">Semester</th><th className="text-left px-5 py-2">Intake</th><th className="text-left px-5 py-2">Status</th><th className="text-left px-5 py-2">Confirmed</th></tr></thead>
            <tbody>
              {data.enrollments.map((e, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="px-5 py-2">Semester {e.semester_number || "—"}</td>
                  <td className="px-5 py-2">{e.intake || "—"}</td>
                  <td className="px-5 py-2">{e.status || "—"}</td>
                  <td className="px-5 py-2 text-slate-500 text-xs">{fmtDate(e.enrollment_confirmation_date)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const Card = ({ label, value }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-4">
    <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
    <div className="text-sm text-slate-900 mt-1">{value || "—"}</div>
  </div>
);
